import React, { useEffect, useState, useCallback, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import PaperWindow from "../components/PaperWindow";
import Navigation from "../components/Navigation";
import ProjectModal from "../components/ProjectModal";
import ContactFormModal from "../components/ContactFormModal";
import AdminProjectModal from "../components/AdminProjectModal";
import { UnderConstructionContent } from "../components/sections/UnderConstruction";
import { BUILT_PROJECTS, BuiltProjectsContent } from "../components/sections/BuiltProjects";
import { ARCHIVE_PROJECTS, ArchiveContent } from "../components/sections/Archive";
import { AboutMeContent } from "../components/sections/AboutMe";
import { ContactContent } from "../components/sections/Contact";
import { useLanguage } from "@/i18n";

const logoSrc = `${process.env.PUBLIC_URL}/assets/LOGO.svg`;

const useIsMobile = () => {
  const [m, setM] = useState(false);
  useEffect(() => {
    const onR = () => setM(window.innerWidth < 900);
    onR();
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  return m;
};

const INITIAL_LAYOUT = {
  "under-construction": { x: 64, y: 120, w: 440, rotate: -1.6, delay: 0.15 },
  "built-projects":     { x: 540, y: 96, w: 440, rotate: 1.2,  delay: 0.25 },
  "archive":            { x: 1010, y: 200, w: 440, rotate: -0.8, delay: 0.35 },
  "about":              { x: 220, y: 560, w: 540, rotate: 0.6,  delay: 0.45 },
  "contact":            { x: 820, y: 600, w: 320, rotate: 2.0,  delay: 0.55 },
};

const ORDER_DEFAULT = ["under-construction", "built-projects", "archive", "about", "contact"];
const CUSTOM_PROJECTS_KEY = "abdulrahman-custom-projects";
const DELETED_PROJECTS_KEY = "abdulrahman-deleted-projects";
const VISITOR_COUNT_KEY = "abdulrahman-visitor-count";
const VISITOR_SESSION_KEY = "abdulrahman-visitor-counted";
const permanentProjectsUrl = `${process.env.PUBLIC_URL}/assets/custom-projects.json`;

const slug = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const projectId = (collection, project) => project.id || `${collection}-${slug(project.title || project.label)}`;

export default function Studio() {
  const { isArabic, lang, t, toggleLanguage } = useLanguage();
  const isMobile = useIsMobile();
  const reduce = useReducedMotion();
  const [order, setOrder] = useState(ORDER_DEFAULT);
  const [minimized, setMinimized] = useState({});
  const [closed, setClosed] = useState({});
  const [activeProject, setActiveProject] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [customProjects, setCustomProjects] = useState([]);
  const [deletedProjectIds, setDeletedProjectIds] = useState([]);
  const [visitorCount, setVisitorCount] = useState(0);
  const boardRef = useRef(null);

  // Apply pencil cursor once
  useEffect(() => {
    document.documentElement.classList.add("pencil-cursor");
    return () => document.documentElement.classList.remove("pencil-cursor");
  }, []);

  useEffect(() => {
    let cancelled = false;

    try {
      const storedProjects = JSON.parse(localStorage.getItem(CUSTOM_PROJECTS_KEY) || "[]");
      const storedDeleted = JSON.parse(localStorage.getItem(DELETED_PROJECTS_KEY) || "[]");
      setCustomProjects(Array.isArray(storedProjects) ? storedProjects : storedProjects.projects || []);
      setDeletedProjectIds(Array.isArray(storedDeleted) ? storedDeleted : []);
    } catch {
      setCustomProjects([]);
      setDeletedProjectIds([]);
    }

    fetch(permanentProjectsUrl)
      .then((response) => (response.ok ? response.json() : []))
      .then((payload) => {
        if (!cancelled) {
          const projects = Array.isArray(payload) ? payload : payload.projects || [];
          const deletedIds = Array.isArray(payload) ? [] : payload.deletedProjectIds || [];
          const localProjects = JSON.parse(localStorage.getItem(CUSTOM_PROJECTS_KEY) || "[]");
          const localDeleted = JSON.parse(localStorage.getItem(DELETED_PROJECTS_KEY) || "[]");
          const normalizedLocalProjects = Array.isArray(localProjects) ? localProjects : localProjects.projects || [];
          const localIds = new Set(normalizedLocalProjects.map((project) => project.id));
          setCustomProjects([...normalizedLocalProjects, ...projects.filter((project) => !localIds.has(project.id))]);
          setDeletedProjectIds([...new Set([...localDeleted, ...deletedIds])]);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const currentCount = Number(localStorage.getItem(VISITOR_COUNT_KEY) || "0");
    if (!sessionStorage.getItem(VISITOR_SESSION_KEY)) {
      const nextCount = currentCount + 1;
      localStorage.setItem(VISITOR_COUNT_KEY, String(nextCount));
      sessionStorage.setItem(VISITOR_SESSION_KEY, "true");
      setVisitorCount(nextCount);
      return;
    }
    setVisitorCount(currentCount);
  }, []);

  const handleSaveCustomProject = (project) => {
    setCustomProjects((current) => {
      const next = [project, ...current.filter((item) => item.id !== project.id)];
      localStorage.setItem(CUSTOM_PROJECTS_KEY, JSON.stringify(next));
      return next;
    });
    setDeletedProjectIds((current) => {
      const next = current.filter((id) => id !== project.id);
      localStorage.setItem(DELETED_PROJECTS_KEY, JSON.stringify(next));
      return next;
    });
  };

  const handleDeleteProject = (project) => {
    const confirmed = window.confirm(`Delete "${project.title}" from the site?`);
    if (!confirmed) return;

    if (project.custom) {
      setCustomProjects((current) => {
        const next = current.filter((item) => item.id !== project.id);
        localStorage.setItem(CUSTOM_PROJECTS_KEY, JSON.stringify(next));
        return next;
      });
      return;
    }

    setDeletedProjectIds((current) => {
      const next = [...new Set([...current, project.id])];
      localStorage.setItem(DELETED_PROJECTS_KEY, JSON.stringify(next));
      return next;
    });
  };

  const handleDownloadCustomProjects = () => {
    const blob = new Blob([JSON.stringify({
      projects: customProjects,
      deletedProjectIds,
    }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "custom-projects.json";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const bringToFront = useCallback((id) => {
    setOrder((prev) => {
      if (prev[prev.length - 1] === id) return prev;
      const next = prev.filter((p) => p !== id);
      next.push(id);
      return next;
    });
  }, []);

  const handleNavigate = (id) => {
    if (closed[id]) setClosed((c) => ({ ...c, [id]: false }));
    if (minimized[id]) setMinimized((m) => ({ ...m, [id]: false }));
    bringToFront(id);
    if (isMobile) {
      const el = document.getElementById(`mobile-${id}`);
      el && el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleClose = (id) => setClosed((c) => ({ ...c, [id]: true }));
  const handleMinimize = (id) => setMinimized((m) => ({ ...m, [id]: !m[id] }));

  const zFor = (id) => 10 + order.indexOf(id);

  const sectionContent = {
    "under-construction": <UnderConstructionContent onOpen={setActiveProject} customProjects={customProjects.filter((project) => project.collection === "pipeline")} deletedProjectIds={deletedProjectIds} />,
    "built-projects": <BuiltProjectsContent onOpen={setActiveProject} customProjects={customProjects.filter((project) => project.collection === "built")} deletedProjectIds={deletedProjectIds} />,
    "archive": <ArchiveContent onOpen={setActiveProject} customProjects={customProjects.filter((project) => project.collection === "archive")} deletedProjectIds={deletedProjectIds} />,
    "about": <AboutMeContent />,
    "contact": <ContactContent onOpenForm={() => setContactOpen(true)} />,
  };

  const sectionMeta = {
    "under-construction": { title: t("projectPipelines"), subtitle: t("tracing"), deco: "tape-double", variant: "tracing" },
    "built-projects":     { title: t("builtProjects"),    subtitle: t("sheet"),   deco: "tape", variant: "paper" },
    "archive":            { title: t("archive"),          subtitle: t("folder"),  deco: "clip", variant: "paper" },
    "about":              { title: t("aboutMe"),          subtitle: t("personal"), deco: "tape", variant: "paper" },
    "contact":            { title: t("contact"),          subtitle: t("pinned"),  deco: "pin",  variant: "paper" },
  };

  const customAdminIds = new Set(customProjects.map((project) => project.id));
  const adminProjects = [
    ...customProjects.map((project) => ({ ...project, custom: true })),
    ...BUILT_PROJECTS
      .map((project) => ({ ...project, id: projectId("built", project), title: project.title, collection: "built" }))
      .filter((project) => !customAdminIds.has(project.id)),
    ...ARCHIVE_PROJECTS
      .map((project) => ({
        ...project,
        id: projectId("archive", project),
        title: project.label,
        collection: "archive",
        image: project.img,
        category: project.tag,
      }))
      .filter((project) => !customAdminIds.has(project.id)),
  ].filter((project) => !deletedProjectIds.includes(project.id));

  return (
    <div ref={boardRef} dir={isArabic ? "rtl" : "ltr"} className="drafting-board relative w-full min-h-screen overflow-hidden">
      {/* Title label */}
      <motion.div
        data-testid="studio-title"
        initial={reduce ? { opacity: 0 } : { opacity: 0, x: -20, rotate: -2 }}
        animate={reduce ? { opacity: 1 } : { opacity: 1, x: 0, rotate: -1.5 }}
        transition={{ delay: 0.05, duration: 0.7 }}
        className={`${isMobile ? "px-5 pt-6 pb-2" : `absolute top-7 ${isArabic ? "right-8" : "left-8"} z-30`}`}
      >
        <div className="flex items-center gap-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-faint">{t("byline")}</p>
          <button
            type="button"
            onClick={toggleLanguage}
            aria-label={lang === "ar" ? "Switch to English" : "Switch to Arabic"}
            className="font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 border border-ink/30 text-ink hover:bg-ink hover:text-paper transition"
          >
            {t("langToggle")}
          </button>
        </div>
        <h1 className="mt-1 flex items-center gap-3 md:gap-4 text-ink">
          <img
            src={logoSrc}
            alt="Abdulrahman Alarifi"
            className="h-28 w-28 md:h-32 md:w-32 object-contain"
          />
          <span className="font-display text-[28px] md:text-[34px] font-medium tracking-tight leading-[1.05]">
            {t("role")}
          </span>
        </h1>
        <p className="font-hand text-2xl text-accentBlue mt-1 rotate-[-2deg] inline-block">
          {t("desk")}
        </p>
      </motion.div>

      <Navigation onNavigate={handleNavigate} isMobile={isMobile} />

      <div className="fixed top-3 left-1/2 z-[85] -translate-x-1/2 border border-ink/20 bg-paper/80 px-3 py-1.5 shadow-sm backdrop-blur font-mono text-[10px] uppercase tracking-[0.18em] text-ink-soft">
        Visitors {visitorCount || 1}
      </div>

      {/* Decorative ruler edge (desktop) */}
      {!isMobile && (
        <div className="ruler-edge absolute top-0 left-0 right-0 h-3 z-20 pointer-events-none" aria-hidden />
      )}

      {/* Decorative annotation arrow */}
      {!isMobile && (
        <svg
          aria-hidden
          className="absolute z-20 pointer-events-none"
          style={{ top: 200, left: 470, width: 90, height: 60 }}
          viewBox="0 0 90 60"
        >
          <path d="M5 50 C 30 10, 60 10, 80 30" fill="none" stroke="#c0532b" strokeWidth="1.4" strokeDasharray="3 3"/>
          <path d="M80 30 L72 26 M80 30 L78 38" fill="none" stroke="#c0532b" strokeWidth="1.4"/>
        </svg>
      )}
      {!isMobile && (
        <span className="font-hand absolute z-20 text-accentRed text-xl rotate-[8deg] pointer-events-none" style={{ top: 178, left: 478 }}>
          {isArabic ? "اسحبني" : "drag me"}
        </span>
      )}

      {/* Mobile: stacked papers */}
      {isMobile ? (
        <div className="px-4 pt-4 pb-12">
          {ORDER_DEFAULT.map((id) => (
            !closed[id] && (
              <div id={`mobile-${id}`} key={id}>
                <PaperWindow
                  id={id}
                  isMobile
                  title={sectionMeta[id].title}
                  subtitle={sectionMeta[id].subtitle}
                  decoration={sectionMeta[id].deco}
                  variant={sectionMeta[id].variant}
                  testId={`window-${id}`}
                >
                  {sectionContent[id]}
                </PaperWindow>
              </div>
            )
          ))}
        </div>
      ) : (
        <div className="relative" style={{ minHeight: 1100 }}>
          {ORDER_DEFAULT.map((id) => {
            if (closed[id]) return null;
            const cfg = INITIAL_LAYOUT[id];
            const meta = sectionMeta[id];
            const isMin = minimized[id];
            return (
              <PaperWindow
                key={id}
                id={id}
                title={meta.title}
                subtitle={meta.subtitle}
                decoration={meta.deco}
                variant={meta.variant}
                z={zFor(id)}
                onFocus={bringToFront}
                onClose={handleClose}
                onMinimize={handleMinimize}
                initial={cfg}
                testId={`window-${id}`}
                className={isMin ? "pointer-events-auto" : ""}
              >
                {!isMin && sectionContent[id]}
                {isMin && (
                  <p className="text-xs font-mono uppercase tracking-[0.18em] text-ink-faint">
                    {t("minimized")}
                  </p>
                )}
              </PaperWindow>
            );
          })}
        </div>
      )}

      {/* Footer note */}
      <div className={`${isMobile ? "px-5 py-6 text-center" : "absolute bottom-4 left-0 right-0 text-center pointer-events-none"} `}>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-faint">
          {t("footer")}
        </p>
      </div>

      <ProjectModal project={activeProject} onClose={() => setActiveProject(null)} />
      <ContactFormModal open={contactOpen} onClose={() => setContactOpen(false)} />
      <AdminProjectModal
        open={adminOpen}
        onClose={() => setAdminOpen(false)}
        onSave={handleSaveCustomProject}
        onDownload={handleDownloadCustomProjects}
        onDelete={handleDeleteProject}
        projectCount={customProjects.length + deletedProjectIds.length}
        projects={adminProjects}
      />
      <button
        type="button"
        onClick={() => setAdminOpen(true)}
        aria-label="Open hidden project editor"
        title="Hidden project editor"
        className="fixed bottom-4 left-4 z-[90] border border-ink/25 bg-paper/70 px-2 py-1 text-ink/60 hover:text-ink hover:bg-paper hover:border-ink/60 transition font-mono text-[10px] uppercase tracking-[0.16em]"
      >
        admin
      </button>
    </div>
  );
}
